import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { graphTrpc } from '@org/clients/graph';
import {
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Input,
  useToast,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@org/ui';
import { ArrowLeft, Send, MoreVertical, Ban, Flag, Check, CheckCheck, User } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useState, useEffect, useRef, useCallback } from 'react';
import { BlockModal } from '@/components/block-modal';
import { ReportModal } from '@/components/report-modal';

export const Route = createFileRoute('/messages/$partnerId')({
  component: ConversationPage,
});

function ConversationPage() {
  const { partnerId } = Route.useParams();
  const { t } = useTranslation('community');
  const { toast } = useToast();
  // Auth context's userId is the Auth0 sub, not the DB UUID. Resolve the
  // current user's DB userId via getMyProfile so own-message detection works.
  const { data: myProfile } = graphTrpc.profiles.getMyProfile.useQuery();
  const myUserId = myProfile?.userId;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const utils = communityTrpc.useUtils();

  const { data, isLoading } = communityTrpc.messages.getConversation.useQuery(
    { partnerId, limit: 100 },
    { refetchInterval: 3000 }
  );

  const sendMessage = communityTrpc.messages.send.useMutation({
    onMutate: async (newMsg) => {
      await utils.messages.getConversation.cancel({ partnerId, limit: 100 });

      const previousData = utils.messages.getConversation.getData({ partnerId, limit: 100 });

      utils.messages.getConversation.setData({ partnerId, limit: 100 }, (old) => {
        if (!old) return old;
        const optimisticMessage = {
          messageId: `optimistic-${Date.now()}`,
          senderId: myUserId ?? '',
          receiverId: partnerId,
          message: newMsg.message,
          imageUrl: newMsg.imageUrl ?? null,
          readAt: null,
          createdAt: new Date(),
        };
        return {
          ...old,
          messages: [...old.messages, optimisticMessage],
        };
      });

      return { previousData };
    },
    onSuccess: () => {
      utils.messages.getConversation.invalidate({ partnerId, limit: 100 });
      utils.messages.getUnreadCount.invalidate();
      utils.messages.listConversations.invalidate();
    },
    onError: (_err, _newMsg, context) => {
      if (context?.previousData) {
        utils.messages.getConversation.setData({ partnerId, limit: 100 }, context.previousData);
      }
      toast({ title: t('common.error'), description: _err.message, variant: 'destructive' });
    },
  });

  const markRead = communityTrpc.messages.markRead.useMutation({
    onSuccess: () => {
      utils.messages.getUnreadCount.invalidate();
      utils.messages.listConversations.invalidate();
    },
  });

  const messages = data?.messages ?? [];
  const partner = data?.partner;

  useEffect(() => {
    if (partnerId) {
      markRead.mutate({ partnerId });
    }
  }, [partnerId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;
    setMessage('');
    sendMessage.mutate({ receiverId: partnerId, message: trimmed });
  };

  const partnerName = partner?.displayName ||
    [partner?.firstName, partner?.lastName].filter(Boolean).join(' ') ||
    t('messages.unknownUser');
  const partnerInitial = partnerName.slice(0, 1).toUpperCase();

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border p-4">
        <div className="flex items-center gap-3">
          <Link to="/messages">
            <Button variant="ghost" size="icon" aria-label={t('common.back')}>
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Button>
          </Link>
          {isLoading ? (
            <Skeleton className="h-10 w-10 rounded-full" />
          ) : (
            <Link to="/profile/$userId" params={{ userId: partnerId }}>
              <Avatar className="h-10 w-10 cursor-pointer">
                <AvatarImage src={partner?.profileImageUrl ?? undefined} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {partnerInitial}
                </AvatarFallback>
              </Avatar>
            </Link>
          )}
          <div>
            {isLoading ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <Link to="/profile/$userId" params={{ userId: partnerId }} className="font-medium hover:underline">
                {partnerName}
              </Link>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('common.moreActions')}>
              <MoreVertical className="h-5 w-5" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/profile/$userId" params={{ userId: partnerId }} className="flex cursor-pointer items-center">
                <User className="mr-2 h-4 w-4" />
                {t('messages.viewProfile')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-amber-600 focus:text-amber-600"
              onSelect={() => setShowReportModal(true)}
            >
              <Flag className="mr-2 h-4 w-4" />
              {t('messages.reportUser')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onSelect={() => setShowBlockModal(true)}
            >
              <Ban className="mr-2 h-4 w-4" />
              {t('messages.blockUser')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3" role="log" aria-label={t('messages.conversation')}>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}
            >
              <Skeleton className="h-10 w-48 rounded-lg" />
            </div>
          ))
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted-foreground">{t('messages.noMessages')}</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwnMessage = msg.senderId === myUserId;
            const isRead = msg.readAt !== null;

            return (
              <div
                key={msg.messageId}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    isOwnMessage
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <p className="break-words">{msg.message}</p>
                  <div
                    className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
                      isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}
                  >
                    <span title={format(new Date(msg.createdAt), 'PPpp')}>
                      {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                    </span>
                    {isOwnMessage && (
                      <span aria-label={isRead ? t('messages.readReceipt') : t('messages.sentReceipt')}>
                        {isRead ? (
                          <CheckCheck className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <Check className="h-3 w-3" aria-hidden="true" />
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 border-t border-border p-4">
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('messages.typeMessage')}
          aria-label={t('messages.typeMessage')}
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <Button
          type="submit"
          size="icon"
          disabled={!message.trim() || sendMessage.isPending}
          aria-label={t('messages.send')}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
        </Button>
      </form>

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetType="user"
        targetId={partnerId}
        targetName={partnerName}
      />

      <BlockModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        userId={partnerId}
        userName={partnerName}
      />
    </div>
  );
}
